import { useState, useRef, useEffect } from "react";
import { useBeneficiaries } from "../../hooks/useBeneficiaries";

const TYPE_META = {
  airtime:     { icon: "ri-phone-line",      emptyLabel: "No saved phone numbers",   idField: "phone"        },
  data:        { icon: "ri-wifi-line",        emptyLabel: "No saved phone numbers",   idField: "phone"        },
  electricity: { icon: "ri-flashlight-line",  emptyLabel: "No saved meter numbers",   idField: "meterNumber"  },
  tv:          { icon: "ri-tv-2-line",        emptyLabel: "No saved smartcard numbers", idField: "smartcardNo"},
};

function timeAgo(ts) {
  const diff = Date.now() - ts;
  const d = Math.floor(diff / 86_400_000);
  if (d === 0) return "Today";
  if (d === 1) return "Yesterday";
  if (d < 7)  return `${d} days ago`;
  return new Date(ts).toLocaleDateString("en-NG", { day: "numeric", month: "short" });
}

export default function BeneficiaryPicker({
  type,
  value,
  onChange,
  onSelect,
  placeholder = "Enter number",
  maxLength,
  numeric    = false,
  inputMode  = "text",
  prefix,
  suffix,
  disabled   = false,
  className  = "",
}) {
  const { beneficiaries, remove } = useBeneficiaries(type);
  const [open,     setOpen]     = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const containerRef = useRef(null);
  const inputRef     = useRef(null);
  const meta         = TYPE_META[type] || TYPE_META.airtime;

  useEffect(() => {
    const handler = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleChange = (e) => {
    let v = e.target.value;
    if (numeric) v = v.replace(/\D/g, "");
    if (maxLength) v = v.slice(0, maxLength);
    onChange(v);
  };

  const handleSelect = (b) => {
    const fieldValue = b[meta.idField] || b.phone || b.meterNumber || b.smartcardNo || "";
    onChange(fieldValue);
    onSelect?.(b);
    setOpen(false);
    inputRef.current?.focus();
  };

  const handleDelete = (e, id) => {
    e.stopPropagation();
    remove(id);
    setDeleteId(null);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className={`bg-surface border-2 rounded-2xl flex items-center px-4 transition-colors ${
        open ? "border-brand" : "border-border-strong"
      }`}>
        {prefix || <i className={`${meta.icon} text-muted mr-3 flex-shrink-0`} />}

        <input
          ref={inputRef}
          type={numeric ? "tel" : "text"}
          inputMode={inputMode}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          className="flex-1 py-4 outline-none text-primary text-sm bg-transparent placeholder:text-faint disabled:opacity-50"
        />

        {suffix ? (
          <div className="flex-shrink-0 ml-2">{suffix}</div>
        ) : (
          <button
            type="button"
            onClick={() => setOpen(v => !v)}
            className="flex-shrink-0 ml-2 w-8 h-8 rounded-xl flex items-center justify-center transition-all relative"
            style={{
              background: open ? "var(--color-brand-light)" : beneficiaries.length > 0 ? "var(--color-surface-hover)" : "transparent",
              color:      open ? "var(--color-brand)" : beneficiaries.length > 0 ? "var(--color-text-secondary)" : "var(--color-text-faint)",
            }}
            title="Saved beneficiaries"
          >
            {beneficiaries.length > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full" style={{ background: "var(--color-brand)" }} />
            )}
            <i className="ri-contacts-book-line text-sm" />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-surface rounded-2xl border border-border shadow-xl z-30 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <p className="text-secondary text-xs font-bold uppercase tracking-wide">Saved Beneficiaries</p>
            <button onClick={() => setOpen(false)} className="text-muted hover:text-secondary">
              <i className="ri-close-line text-lg" />
            </button>
          </div>

          {beneficiaries.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <i className="ri-user-add-line text-faint text-3xl mb-2 block" />
              <p className="text-muted text-sm">{meta.emptyLabel}</p>
              <p className="text-faint text-xs mt-1">Saved automatically after each transaction</p>
            </div>
          ) : (
            <div className="max-h-56 overflow-y-auto divide-y divide-border">
              {beneficiaries.map(b => {
                const identifier = b[meta.idField] || b.phone || b.meterNumber || b.smartcardNo || "";
                const isSelected = value === identifier;

                return (
                  <div key={b.id}
                    onClick={() => deleteId === b.id ? setDeleteId(null) : handleSelect(b)}
                    className="flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors"
                    style={{ background: isSelected ? "var(--color-brand-light)" : "transparent" }}
                  >
                    <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: isSelected ? "var(--color-brand-light)" : "var(--color-surface-hover)" }}>
                      <i className={`${meta.icon} text-base`} style={{ color: isSelected ? "var(--color-brand)" : "var(--color-text-muted)" }} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: isSelected ? "var(--color-brand-text)" : "var(--color-text-primary)" }}>
                        {identifier}
                      </p>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {b.label && b.label !== identifier && (
                          <p className="text-muted text-xs truncate">{b.label}</p>
                        )}
                        {(b.network || b.disco || b.provider) && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-surface-hover text-muted flex-shrink-0">
                            {b.network || b.disco || b.provider}
                          </span>
                        )}
                        {b.meterType && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-surface-hover text-muted flex-shrink-0 capitalize">
                            {b.meterType}
                          </span>
                        )}
                        <p className="text-faint text-[10px] flex-shrink-0">{timeAgo(b.lastUsed)}</p>
                      </div>
                    </div>

                    {deleteId === b.id ? (
                      <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                        {/* Fix: was bg-danger + text-on-brand — text-on-brand is
                            contrast-tuned for --color-brand specifically, not
                            --color-danger, so this read low-contrast/wrong in
                            dark mode. text-on-danger is calibrated for this fill. */}
                        <button onClick={e => handleDelete(e, b.id)}
                          className="text-[10px] font-bold px-2 py-1 rounded-lg bg-danger text-on-danger">
                          Remove
                        </button>
                        <button onClick={() => setDeleteId(null)}
                          className="text-[10px] px-2 py-1 rounded-lg bg-surface-hover text-muted">
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={e => { e.stopPropagation(); setDeleteId(b.id); }}
                        className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-faint hover:text-danger transition-colors"
                      >
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
